import { GroupService } from './../../../../services/group/group.service';
import { Location } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { LS } from 'src/app/helpers/localStorage.enum';
import { FileService } from 'src/app/services/file/file.service';
import { OrganisationService } from 'src/app/services/organisation/organisation.service';
import { RoleService } from 'src/app/services/role/role.service';
import { SwalService } from 'src/app/services/swal/swal.service';
import {
  getCurrentUser,
  UserService,
} from 'src/app/services/user/user.service';
import { DataService } from 'src/app/services/data/data.service';
import { regexValidator } from 'src/app/helpers/customFormValidators';

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit.component.html',
  styleUrls: ['./edit.component.sass'],
})
export class EditComponent implements OnInit, AfterViewInit, OnDestroy {
  /** Password element */
  @ViewChild('password') passwordEle: ElementRef;

  public form: FormGroup;
  id;
  avatar;
  roles;
  organisations;
  languages;
  groups;
  userServiceSub: Subscription;
  fileServiceSub: Subscription;
  roleServiceSub: Subscription;
  organisationServiceSub: Subscription;
  groupServiceSub: Subscription;
  userServiceUpdateSub: Subscription;
  userServiceRemoveSub: Subscription;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  unsub$ = new Subject();
  submitted: boolean = false;
  passwordIsFocus: boolean;
  get passwordControl() {
    return this.form.get('password');
  }

  constructor(
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private activatedRouter: ActivatedRoute,
    private router: Router,
    private location: Location,
    private userService: UserService,
    private fileService: FileService,
    private roleService: RoleService,
    private organisationService: OrganisationService,
    private groupService: GroupService,
    private toastr: ToastrService,
    private swalService: SwalService,
    private _t: TranslateService,
    private translateService: TranslateService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.initForm();

    const currentUser = getCurrentUser();

    /** currentUser.user_id is intended for route /profile/edit, while snapshot is for /user/edit/:id */
    this.id =
      this.activatedRouter.snapshot.paramMap.get('id') || currentUser.user_id;
    this.roles = [];
    this.userServiceSub = this.userService.findOne(this.id).subscribe(
      (res) => {
        this.patchForm(res);
      },
      (error) => {
        this.userService.findOwn().subscribe((res) => {
          this.patchForm(res);
        });
      }
    );

    this.roleServiceSub = this.roleService.findAll().subscribe((res) => {
      const currentUser = getCurrentUser(),
        su = 'SuperAdmin',
        initRolesList = () => {
          this.isSuperAdmin = currentUser.roles.includes(su);
          this.isAdmin =
            currentUser.roles.includes('Admin') || this.isSuperAdmin;
          if (!this.isSuperAdmin) res = res.filter((ele) => ele !== su);
          this.roles = res;
        };

      /** This is fallback for users that haven't logged out */
      if (!currentUser?.roles?.length) {
        this.userService
          .findOne(currentUser.user_id)
          .pipe(takeUntil(this.unsub$.asObservable()))
          .subscribe((user) => {
            const { roles } = user;

            if (!roles?.length) return;

            currentUser.roles = roles;
            // localStorage.setItem(LS.currentUser, JSON.stringify(currentUser))
            this.dataService.setLocalStorage(
              LS.currentUser,
              JSON.stringify(currentUser)
            );

            initRolesList();
          });
      } else {
        initRolesList();
      }
      this.roles = res;

      if (this.isSuperAdmin) {
        this.organisationServiceSub = this.organisationService
          .findAll()
          .subscribe((res) => {
            this.organisations = res.data || [];
          });

        // this.groupServiceSub = this.groupService.findGroups(this.id).subscribe((res) => {
        //   this.groups = res;
        // })
        this.groupService.findAll().subscribe((g) => {
          this.groups = g.data.map((i, k) => {
            return { ...i, member: i.members.includes(this.id) };
          });
        });
      }
    });

    this.languages = [
      { def_lang: 'en', name: 'English' },
      { def_lang: 'jp', name: '日本語' },
    ];

    if (!this.isSuntory) {
      this.languages.push({ def_lang: 'de', name: 'Deutsch' });
    }
  }
  get isSuntory() {
    return location?.hostname.includes('suntory');
  }
  ngAfterViewInit(): void {
    this.activatedRouter.queryParams
      .pipe(takeUntil(this.unsub$.asObservable()))
      .subscribe((qParams) => {
        const { focus } = qParams;

        if (focus && focus === 'password') {
          /** setTimeout added to resolve 'ExpressionChangedAfterItHasBeenCheckedError' */
          setTimeout(() => {
            this.passwordEle.nativeElement.focus();
          }, 100);
        }
      });
  }

  onFocusChange(isFocus: boolean) {
    this.passwordIsFocus = isFocus;
  }

  patchForm(res) {
    const defaultOrganisation = this.isAdmin ? res.orgarnisation?.id : '';
    this.form.patchValue({
      fullname: res.fullname ? res.fullname : '',
      username: res.username ? res.username : '',
      email: res.email ? res.email : '',
      role: res.roles ? res.roles[0] : '',
      organisation: res.organisation?.id || defaultOrganisation,
      def_lang: res.preferences?.def_lang || '',
      contact_detail: res.contact_detail ? res.contact_detail : '',
      job_title: res.job_title ? res.job_title : '',
      office_address: res.office_address ? res.office_address : '',
      facilityCodeAccessLogin: res.facilityCodeAccessLogin
        ? res.facilityCodeAccessLogin
        : '',
    });

    if (res.avatar?.id) {
      this.fileServiceSub = this.fileService
        .findOne(res.avatar.id)
        .subscribe((res) => {
          this.avatar = `url("${
            res?.url_small || res?.url || '/assets/images/avatar.svg'
          }")`;
        });
    } else {
      this.avatar = 'url(/assets/images/avatar.svg)';
    }
  }

  initForm() {
    this.form = this.formBuilder.group({
      fullname: ['', Validators.required],
      username: ['', Validators.required],
      // password: ['', Validators.required],
      password: new FormControl(''),
      role: '',
      organisation: '',
      def_lang: '',
      email: ['', Validators.required],
      contact_detail: '',
      job_title: '',
      office_address: '',
      facilityCodeAccessLogin: '',
    });

    this.passwordControl.valueChanges.subscribe((password) => {
      if (password) {
        this.passwordControl.setValidators([
          Validators.required,
          Validators.minLength(8),
          regexValidator(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\W])(?=.{8,})/
          ),
          regexValidator(/^(?=.*[a-z])/, 'lowerRequired'),
          regexValidator(/^(?=.*[A-Z])/, 'upperRequired'),
          regexValidator(/^(?=.*[0-9])/, 'numberRequired'),
          regexValidator(/^(?=.*[\W])/, 'specialRequired'),
        ]);
      } else {
        this.passwordControl.setValidators(null);
      }
    });
  }

  onSubmit() {
    // this.spinner.show();
    if (this.isSuperAdmin || this.isAdmin)
      this.form.value.roles = [this.form.value.role];
    this.form.value.username = this.form.value.username.trim();
    let formData = this.form.value;
    if (!this.isSuperAdmin) {
      delete formData.organisation;
    }
    this.submitted = true;
    if (this.form.invalid) {
      return;
    }
    this.spinner.show();
    this.userServiceUpdateSub = this.userService
      .update(this.id, formData, this.isAdmin)
      .subscribe(
        async (res) => {
          const currentUser = getCurrentUser();

          if (this.groups) {
            await this.groupService
              .updateGroups(this.id, this.groups)
              .toPromise();
            console.log(this.groups);
          }

          this.spinner.hide();
          if (this.isAdmin) {
            this.router.navigate(['users']);
          } else {
            this.router.navigate(['profile']);
          }

          if (currentUser.user_id === res.id) {
            let currentUserFromLocalStorage = JSON.parse(
              localStorage.getItem(LS.currentUser)
            );
            currentUserFromLocalStorage.organisation = res.organisation;
            currentUserFromLocalStorage.username = res.username;
            currentUserFromLocalStorage.roles = res.roles;
            currentUserFromLocalStorage.permissions = res.permissions;
            // localStorage.setItem(LS.currentUser,JSON.stringify(currentUserFromLocalStorage));
            this.dataService.setLocalStorage(
              LS.currentUser,
              JSON.stringify(currentUserFromLocalStorage)
            );

            if (res.preferences?.def_lang) {
              const defLang = res.preferences.def_lang;
              this.translateService.use(defLang);
              localStorage.setItem(LS.defaultLang, defLang);
            }
          }
        },
        ({ error }) => {
          this.spinner.hide();
          try {
            const msg = error.message
              .reduce((results, u) => ((results += u + ', '), results), '')
              .slice(0, -2);
            this.toastr.error(msg, this._t.instant('Error'));
          } catch (err) {
            const msg = error.message;
            this.toastr.error(this._t.instant(msg), this._t.instant('Error'));
          }
        }
      );
  }

  onDiscard() {
    this.location.back();
  }

  onRemove() {
    this.swalService.confirmDialog((swalRes) => {
      this.spinner.show();
      this.userServiceRemoveSub = this.userService
        .remove(this.id)
        .subscribe((res) => {
          this.spinner.hide();
          const undoMsg = this._t.instant('Tap HERE to undo');
          this.toastr
            .info(undoMsg, '', {
              progressBar: true,
              timeOut: 7000,
              enableHtml: true,
            })
            .onTap.pipe(take(1))
            .subscribe(() => {
              this.userService.restore(this.id).subscribe();
            });
          this.router.navigate(['users']);
        });
    });
  }

  ngOnDestroy() {
    this.userServiceSub.unsubscribe();
    this.unsub$.next();
    this.unsub$.unsubscribe();
    if (this.fileServiceSub) this.fileServiceSub.unsubscribe();
    if (this.userServiceUpdateSub) this.userServiceUpdateSub.unsubscribe();
    if (this.roleServiceSub) this.roleServiceSub.unsubscribe();
    if (this.organisationServiceSub) this.organisationServiceSub.unsubscribe();
    if (this.groupServiceSub) this.groupServiceSub.unsubscribe();
  }
}
